
import { useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { Profile } from '@/hooks/useProfile';
import { useAvatarUpload } from './hooks/useAvatarUpload';
import { useProfileItems } from './hooks/useProfileItems';
import { usePortfolioExperience } from './hooks/useProfileItems';
import { useProfileSubmit } from './hooks/useProfileSubmit';
import { useProfileFormState } from './hooks/useProfileFormState';

export const useProfileForm = (
  user: User,
  initialProfile: Profile,
  onProfileUpdate: (updatedProfile: Profile) => void,
  targetUserId?: string
) => {
  const [formError, setFormError] = useState<string | null>(null);

  // Use the profile form state hook to manage the profile state
  const { profile, updateProfileField, updateProfile } = useProfileFormState(initialProfile);

  // Use the specialized hooks
  const {
    avatarPreview,
    handleAvatarChange,
    uploadAvatar,
    setAvatarFile,
    isUploading
  } = useAvatarUpload(user, initialProfile.avatar_url, targetUserId);

  const {
    skills,
    languages,
    newSkill,
    newLanguage,
    setNewSkill,
    setNewLanguage,
    addSkill,
    removeSkill,
    addLanguage,
    removeLanguage
  } = useProfileItems(initialProfile.skills, initialProfile.languages);

  // Use the dedicated hook for portfolio and experience items
  const {
    portfolioItems,
    experienceItems,
    handlePortfolioChange,
    handleExperienceChange
  } = usePortfolioExperience(initialProfile.portfolio_items, initialProfile.experience_items);

  // Sync state from specialized hooks with main profile state
  useEffect(() => {
    updateProfileField('skills', skills);
  }, [skills]);

  useEffect(() => {
    updateProfileField('languages', languages);
  }, [languages]);

  useEffect(() => {
    updateProfileField('portfolio_items', portfolioItems);
  }, [portfolioItems]);

  useEffect(() => {
    updateProfileField('experience_items', experienceItems);
  }, [experienceItems]);

  // Form submission logic
  const {
    isUpdating,
    validateForm,
    handleSubmit: submitProfile
  } = useProfileSubmit(user, profile, onProfileUpdate, uploadAvatar, setAvatarFile, targetUserId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Validate the form
    const validationError = validateForm(profile);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    // Submit the form if validation passes
    await submitProfile(e);
  };

  return {
    profile,
    setProfile: updateProfileField,
    formError,
    isUpdating,
    isUploading,
    avatarPreview,
    newSkill,
    newLanguage,
    handleAvatarChange,
    addSkill,
    removeSkill,
    addLanguage,
    removeLanguage,
    handlePortfolioChange,
    handleExperienceChange,
    handleSubmit,
    setNewSkill,
    setNewLanguage
  };
};

export default useProfileForm;
